import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { KEYS_SAVED_EVENT } from '../BrokerKeyRotationBanner';
import { IconCheck, IconPencil, IconRefresh, IconTrash, IconUpload, IconX } from '../Icons';
import ManualTradeModal from './ManualTradeModal';
import {
  brokerSync,
  brokerSyncCsv,
  fetchBrokerStatus,
  prepareNinjaTraderOAuth,
  removeBrokerAccount,
} from '../../services/vectorDashboardApi';
import { getBrokerSetup } from './brokerSetupInstructions';
import { getBrandIdentity, useBrandIdentity } from '../../utils/brandIdentity';
import NinjaTraderConnectPortal from './NinjaTraderConnectPortal';
import SnaptradeConnectPanel from './SnaptradeConnectPanel';
import BrokerPicker from './BrokerPicker';

const BROKERS = [
  {
    id: 'coinbase',
    label: 'Coinbase',
    shortName: 'CB',
    tone: 'coinbase',
    logoSrc: '/brokers/coinbase.webp',
    description: 'Advanced Trade API keys',
  },
  {
    id: 'alpaca',
    label: 'Alpaca',
    shortName: 'A',
    tone: 'alpaca',
    logoSrc: '/brokers/alpaca.png',
    description: 'Paper or live trading API',
  },
  {
    id: 'ibkr',
    label: 'Interactive Brokers',
    shortName: 'IB',
    tone: 'ibkr',
    logoSrc: '/brokers/ibkr.png',
    description: 'Flex Web Service report',
  },
  {
    id: 'ninjatrader',
    label: 'Ninja Trader',
    shortName: 'NT',
    tone: 'ninjatrader',
    logoSrc: '/brokers/ninjatrader.jpg',
    description: 'Tradovate OAuth or API credentials',
  },
  {
    // Many brokerages through SnapTrade; its own panel (SnaptradeConnectPanel).
    id: 'snaptrade',
    label: 'Schwab, Robinhood & more',
    shortName: '+',
    tone: 'snaptrade',
    description: 'Charles Schwab, Robinhood, Webull, Fidelity and more',
  },
];

const SAVED_CREDENTIAL_MASK = '**********';

function isMaskedValue(value) {
  return value === SAVED_CREDENTIAL_MASK;
}

function BrokerLogo({ broker, size = 'md' }) {
  const className = `vd-broker-logo ${broker.tone}${broker.logoSrc ? ' has-image' : ''}${size === 'lg' ? ' lg' : ''}`;

  if (broker.logoSrc) {
    return (
      <span className={className} aria-hidden="true">
        <img src={broker.logoSrc} alt="" />
      </span>
    );
  }

  return (
    <span className={className} aria-hidden="true">
      {broker.shortName}
    </span>
  );
}

function BrokerSetupInstructions({ brokerId }) {
  const { appName } = useBrandIdentity();
  const setup = getBrokerSetup(appName)[brokerId];
  if (!setup) return null;

  return (
    <details className="vd-broker-instructions">
      <summary>Setup instructions</summary>
      <div className="vd-broker-instructions-body">
        <ol className="vd-broker-instructions-steps">
          {setup.steps.map((step) => (
            <li key={step.title}>
              <span>{step.title}</span>
              {step.bullets?.length > 0 && (
                <ul>
                  {step.bullets.map((bullet, i) => {
                    if (typeof bullet === 'string') {
                      return <li key={i}>{bullet}</li>;
                    }
                    return (
                      <li key={i}>
                        {bullet.text}
                        {bullet.link && (
                          <>
                            {' '}
                            <a
                              className="vd-broker-instructions-link"
                              href={bullet.link.href}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {bullet.link.label}
                            </a>
                          </>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </li>
          ))}
        </ol>
        {setup.note && <p className="vd-muted vd-broker-instructions-note">{setup.note}</p>}
      </div>
    </details>
  );
}

/**
 * returnPage: where SnapTrade's and Tradovate's sign-in pages send the person
 * back to ('client-reporting' = My Portfolio, or 'trade-watcher').
 */
export default function TradeIntegrationModal({ isOpen, onClose, onSynced, initialBrokerId = null, returnPage = 'client-reporting' }) {
  const { appName } = useBrandIdentity();
  const [view, setView] = useState('picker');
  const [brokerId, setBrokerId] = useState(null);
  const [brokerStatus, setBrokerStatus] = useState({});
  const [tradovateOAuthConfigured, setTradovateOAuthConfigured] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const [coinbase, setCoinbase] = useState({ apiKeyName: '', apiSecret: '', authType: 'ed25519' });
  const [alpaca, setAlpaca] = useState({ apiKey: '', apiSecret: '', isPaper: true });
  const [ibkr, setIbkr] = useState({ flexToken: '', flexQueryId: '' });
  const [ninja, setNinja] = useState({
    username: '',
    password: '',
    apiCid: '',
    apiSec: '',
    appId: 'RiskLo',
    isDemo: false,
  });
  const [manualModalOpen, setManualModalOpen] = useState(false);
  const [ninjaView, setNinjaView] = useState('portal');
  const csvInputRef = useRef(null);

  const selectedBroker = useMemo(
    () => BROKERS.find((b) => b.id === brokerId) || null,
    [brokerId],
  );
  const selectedStatus = brokerStatus[brokerId] || {};

  const loadStatus = useCallback(async () => {
    try {
      const res = await fetchBrokerStatus();
      setBrokerStatus(res.brokers || {});
      setTradovateOAuthConfigured(res.tradovateOAuthConfigured === true);
      return res.brokers || {};
    } catch (e) {
      console.error(e);
      return {};
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    setError('');
    setMessage('');
    loadStatus().then((status) => {
      if (initialBrokerId) {
        resetBrokerForms(initialBrokerId, status[initialBrokerId] || {});
        setBrokerId(initialBrokerId);
        setView('detail');
      } else {
        setView('picker');
        setBrokerId(null);
      }
    });
  }, [isOpen, initialBrokerId, loadStatus]);

  if (!isOpen) return null;

  function resetBrokerForms(id, status = {}) {
    const configured = Boolean(status.configured);
    if (id === 'coinbase') {
      setCoinbase(configured
        ? {
          apiKeyName: SAVED_CREDENTIAL_MASK,
          apiSecret: SAVED_CREDENTIAL_MASK,
          authType: status.authType === 'ed25519' ? 'ed25519' : 'ecdsa',
        }
        : { apiKeyName: '', apiSecret: '', authType: 'ed25519' });
    } else if (id === 'alpaca') {
      setAlpaca(configured
        ? {
          apiKey: SAVED_CREDENTIAL_MASK,
          apiSecret: SAVED_CREDENTIAL_MASK,
          isPaper: status.isPaper !== false,
        }
        : { apiKey: '', apiSecret: '', isPaper: true });
    } else if (id === 'ibkr') {
      setIbkr(configured
        ? { flexToken: SAVED_CREDENTIAL_MASK, flexQueryId: SAVED_CREDENTIAL_MASK }
        : { flexToken: '', flexQueryId: '' });
    } else if (id === 'ninjatrader') {
      setNinja(configured
        ? {
          username: SAVED_CREDENTIAL_MASK,
          password: SAVED_CREDENTIAL_MASK,
          apiCid: SAVED_CREDENTIAL_MASK,
          apiSec: SAVED_CREDENTIAL_MASK,
          appId: 'RiskLo',
          isDemo: status.isDemo !== false,
        }
        : {
          username: '',
          password: '',
          apiCid: '',
          apiSec: '',
          appId: 'RiskLo',
          isDemo: false,
        });
    }
  }

  function clearMaskedField(setter, field) {
    setter((prev) => (isMaskedValue(prev[field]) ? { ...prev, [field]: '' } : prev));
  }

  function buildCoinbasePayload() {
    if (!selectedStatus.configured) return coinbase;
    if (isMaskedValue(coinbase.apiKeyName) && isMaskedValue(coinbase.apiSecret)) return {};
    return coinbase;
  }

  function buildAlpacaPayload() {
    if (!selectedStatus.configured) return alpaca;
    const usingSavedKeys = isMaskedValue(alpaca.apiKey) && isMaskedValue(alpaca.apiSecret);
    if (usingSavedKeys) return { isPaper: alpaca.isPaper };
    return alpaca;
  }

  function buildIbkrPayload() {
    if (!selectedStatus.configured) return ibkr;
    if (isMaskedValue(ibkr.flexToken) && isMaskedValue(ibkr.flexQueryId)) return {};
    return ibkr;
  }

  function buildNinjaPayload() {
    if (!selectedStatus.configured) return ninja;
    const usingSaved = isMaskedValue(ninja.username)
      && isMaskedValue(ninja.password)
      && isMaskedValue(ninja.apiCid)
      && isMaskedValue(ninja.apiSec);
    if (usingSaved) return { isDemo: ninja.isDemo, appId: ninja.appId || 'RiskLo' };
    return ninja;
  }

  function openBrokerDetail(id) {
    const status = brokerStatus[id] || {};
    resetBrokerForms(id, status);
    setBrokerId(id);
    setNinjaView('portal');
    if (id === 'ninjatrader') {
      const connections = status.connections || {};
      if (connections.demo?.configured && !connections.live?.configured) {
        setNinja((prev) => ({ ...prev, isDemo: true }));
      } else if (connections.live?.configured) {
        setNinja((prev) => ({ ...prev, isDemo: false }));
      }
    }
    setView('detail');
    setError('');
    setMessage('');
  }

  function backToPicker() {
    setView('picker');
    setBrokerId(null);
    setError('');
    setMessage('');
  }

  async function handleNinjaOAuthConnect() {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const frontendUrl = `${window.location.origin}/${returnPage === 'trade-watcher' ? 'trade-watcher' : 'client-reporting'}`;
      const res = await prepareNinjaTraderOAuth({
        frontendUrl,
        environment: ninja.isDemo ? 'demo' : 'live',
      });
      if (!res.authUrl) {
        throw new Error(res.error || 'Could not start Tradovate sign-in');
      }
      window.location.href = res.authUrl;
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  }

  async function handleConnect() {
    if (!brokerId) return;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      if (brokerId === 'coinbase') {
        const res = await brokerSync('coinbase', buildCoinbasePayload());
        setMessage(`Synced ${res.synced || 0} Coinbase trades`);
      } else if (brokerId === 'alpaca') {
        const res = await brokerSync('alpaca', buildAlpacaPayload());
        setMessage(`Synced ${res.synced || 0} Alpaca trades`);
      } else if (brokerId === 'ibkr') {
        const res = await brokerSync('ibkr', buildIbkrPayload());
        setMessage(res.message || `Synced ${res.synced || 0} IBKR trades`);
      } else if (brokerId === 'ninjatrader') {
        const res = await brokerSync('ninjatrader', buildNinjaPayload());
        const modeLabel = ninja.isDemo ? 'Demo' : 'Live';
        const synced = res.synced || 0;
        if (synced === 0 && Array.isArray(res.accounts) && res.accounts.length) {
          const accountNotes = res.accounts
            .map((entry) => `${entry.name || entry.accountId}: ${entry.synced || 0} trades`)
            .join('; ');
          setMessage(
            `${modeLabel} balance synced, but Tradovate returned no closed trades (${accountNotes}). `
            + 'Open positions and account fees can still change net liquidity.',
          );
        } else {
          setMessage(`Synced ${synced} ${modeLabel} NinjaTrader trades`);
        }
      }
      const latestStatus = await loadStatus();
      resetBrokerForms(brokerId, latestStatus[brokerId] || { configured: true });
      // New Alpaca / Coinbase keys clear the "replace your API key" prompt.
      if (brokerId === 'alpaca' || brokerId === 'coinbase') window.dispatchEvent(new Event(KEYS_SAVED_EVENT));
      onSynced?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function handleManualSubmit(manual) {
    if (!selectedBroker) return;
    setBusy(true);
    setError('');
    try {
      const res = await brokerSync('manual', {
        broker: selectedBroker.label,
        symbol: manual.symbol,
        assetType: 'Stock',
        executions: [{
          id: '1',
          dateTime: manual.dateTime || new Date().toISOString(),
          side: manual.side,
          qty: manual.qty,
          price: manual.price,
          comm: '0',
          fees: '0',
        }],
      });
      setMessage(res.message || `Saved ${res.synced || 0} manual trades`);
      setManualModalOpen(false);
      await loadStatus();
      onSynced?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function handleCsvSelected(event) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !brokerId) return;

    setBusy(true);
    setError('');
    setMessage('');
    try {
      const csvText = await file.text();
      const res = await brokerSyncCsv(brokerId, csvText);
      setMessage(`Imported ${res.synced || 0} trades from CSV`);
      await loadStatus();
      onSynced?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function handleRemoveAccount(modeOverride) {
    if (!selectedBroker) return;

    const modeLabel = modeOverride === 'demo' || (modeOverride !== 'live' && ninja.isDemo) ? 'Demo' : 'Live';
    const confirmed = window.confirm(
      selectedBroker.id === 'ninjatrader'
        ? `Remove ${modeLabel} NinjaTrader connection? This deletes synced ${modeLabel.toLowerCase()} trades from ${getBrandIdentity().appName}.`
        : `Remove ${selectedBroker.label}? This deletes the saved broker connection and synced trades from ${getBrandIdentity().appName}.`,
    );
    if (!confirmed) return;

    setBusy(true);
    setError('');
    setMessage('');
    try {
      const accountId = selectedBroker.id === 'ninjatrader'
        ? (modeOverride === 'demo' || modeOverride === 'live'
          ? modeOverride
          : (ninja.isDemo ? 'demo' : 'live'))
        : 'default';
      const res = await removeBrokerAccount({ broker: selectedBroker.id, accountId });
      setMessage(
        selectedBroker.id === 'ninjatrader'
          ? `Removed ${modeLabel} connection (${res.removedTrades || 0} trades).`
          : `Removed ${res.removedTrades || 0} synced trades and disconnected the account.`,
      );
      resetBrokerForms(selectedBroker.id);
      await loadStatus();
      onSynced?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="vd-modal-backdrop" role="dialog" aria-modal="true">
      <div className={`vd-modal vd-broker-modal${brokerId === 'ninjatrader' && ninjaView === 'portal' ? ' vd-ninja-connect-modal' : ''}`}>
        {view === 'picker' ? (
          <div key="picker" className="vd-modal-view">
            <header>
              <div>
                <h3>Connect / Sync Broker</h3>
                <p className="vd-muted">Search for your broker, or pick one below.</p>
              </div>
              <button type="button" className="vd-modal-close" onClick={onClose} aria-label="Close">
                <IconX size={18} />
              </button>
            </header>

            <BrokerPicker
              directBrokers={BROKERS.filter((b) => b.id !== 'snaptrade')}
              brokerStatus={brokerStatus}
              onOpenDirect={openBrokerDetail}
              onManageLinked={() => openBrokerDetail('snaptrade')}
              returnTo={returnPage === 'trade-watcher' ? 'trade-watcher' : null}
            />
          </div>
        ) : (
          <div key={`detail-${brokerId}`} className="vd-modal-view">
            {brokerId === 'snaptrade' ? (
              <SnaptradeConnectPanel
                returnTo={returnPage === 'trade-watcher' ? 'trade-watcher' : null}
                status={selectedStatus}
                onBack={backToPicker}
                onClose={onClose}
                onChanged={async () => {
                  await loadStatus();
                  onSynced?.();
                }}
              />
            ) : brokerId === 'ninjatrader' && ninjaView === 'portal' ? (
              <>
                <header className="vd-broker-detail-header vd-ninja-connect-header">
                  <button type="button" className="vd-back-btn" onClick={backToPicker}>
                    ← All brokers
                  </button>
                  <button type="button" className="vd-modal-close" onClick={onClose} aria-label="Close">
                    <IconX size={18} />
                  </button>
                </header>

                <NinjaTraderConnectPortal
                  busy={busy}
                  tradovateOAuthConfigured={tradovateOAuthConfigured}
                  isDemo={ninja.isDemo}
                  onDemoChange={(nextDemo) => setNinja((prev) => ({ ...prev, isDemo: nextDemo }))}
                  onOAuthConnect={handleNinjaOAuthConnect}
                  onUseApiKeys={() => {
                    setNinjaView('apikey');
                    setError('');
                    setMessage('');
                  }}
                  connections={selectedStatus.connections || {}}
                  onSync={handleConnect}
                  onRemove={() => handleRemoveAccount(ninja.isDemo ? 'demo' : 'live')}
                  error={error}
                  message={message}
                />

                <div className="vd-manual-import-row vd-ninja-connect-imports">
                  <input
                    ref={csvInputRef}
                    type="file"
                    accept=".csv,text/csv"
                    className="vd-hidden-file-input"
                    onChange={handleCsvSelected}
                  />
                  <button
                    type="button"
                    className="vd-manual-import-btn"
                    disabled={busy}
                    onClick={() => csvInputRef.current?.click()}
                  >
                    <IconUpload size={18} />
                    <span>
                      <strong>Upload CSV</strong>
                      <small className="vd-muted">Symbol, Side, Qty, Price, Date/Time columns</small>
                    </span>
                  </button>
                  <button
                    type="button"
                    className="vd-manual-import-btn"
                    disabled={busy}
                    onClick={() => setManualModalOpen(true)}
                  >
                    <IconPencil size={18} />
                    <span>
                      <strong>Manual entry</strong>
                      <small className="vd-muted">Add a single round-trip trade</small>
                    </span>
                  </button>
                </div>

                <ManualTradeModal
                  isOpen={manualModalOpen}
                  brokerLabel={selectedBroker?.label}
                  onClose={() => setManualModalOpen(false)}
                  onSubmit={handleManualSubmit}
                  busy={busy}
                />
              </>
            ) : (
              <>
            <header className="vd-broker-detail-header">
              <div className="vd-broker-detail-title">
                <button
                  type="button"
                  className="vd-back-btn"
                  onClick={() => {
                    if (brokerId === 'ninjatrader' && ninjaView === 'apikey') {
                      setNinjaView('portal');
                      setError('');
                      setMessage('');
                      return;
                    }
                    backToPicker();
                  }}
                >
                  {brokerId === 'ninjatrader' && ninjaView === 'apikey' ? '← OAuth connection' : '← All brokers'}
                </button>
                <div className="vd-broker-setup-head">
                  <BrokerLogo broker={selectedBroker} />
                  <div>
                    <h3>{selectedBroker.label}</h3>
                    <p className="vd-muted">{getBrokerSetup(appName)[brokerId]?.summary || selectedBroker.description}</p>
                  </div>
                </div>
              </div>
              <button type="button" className="vd-modal-close" onClick={onClose} aria-label="Close">
                <IconX size={18} />
              </button>
            </header>

            <BrokerSetupInstructions brokerId={brokerId} />

            {selectedStatus.configured && (
              <div className="vd-connected-summary">
                <span><IconCheck size={16} /> Connected</span>
                <span>{selectedStatus.tradeCount || 0} synced trades</span>
              </div>
            )}

            {brokerId === 'coinbase' && (
              <div className="vd-form-stack">
                <span className="vd-control-label">Key type</span>
                <div className="vd-toggle-group" role="group" aria-label="Coinbase key type">
                  <button
                    type="button"
                    className={`vd-toggle ${coinbase.authType === 'ed25519' ? 'active' : ''}`}
                    onClick={() => setCoinbase({ ...coinbase, authType: 'ed25519' })}
                  >
                    Ed25519 (Recommended)
                  </button>
                  <button
                    type="button"
                    className={`vd-toggle ${coinbase.authType === 'ecdsa' ? 'active' : ''}`}
                    onClick={() => setCoinbase({ ...coinbase, authType: 'ecdsa' })}
                  >
                    ECDSA (Legacy)
                  </button>
                </div>
                <input
                  placeholder="API Key Name"
                  value={coinbase.apiKeyName}
                  onFocus={() => clearMaskedField(setCoinbase, 'apiKeyName')}
                  onChange={(e) => setCoinbase({ ...coinbase, apiKeyName: e.target.value })}
                />
                <textarea
                  placeholder={coinbase.authType === 'ed25519'
                    ? 'API Secret — Ed25519 private key (single base64 line)'
                    : 'API Secret — EC private key (PEM, includes BEGIN/END lines)'}
                  value={coinbase.apiSecret}
                  onFocus={() => clearMaskedField(setCoinbase, 'apiSecret')}
                  onChange={(e) => setCoinbase({ ...coinbase, apiSecret: e.target.value })}
                  rows={4}
                />
              </div>
            )}

            {brokerId === 'alpaca' && (
              <div className="vd-form-stack">
                <input
                  placeholder="API Key"
                  value={alpaca.apiKey}
                  onFocus={() => clearMaskedField(setAlpaca, 'apiKey')}
                  onChange={(e) => setAlpaca({ ...alpaca, apiKey: e.target.value })}
                />
                <input
                  placeholder="API Secret"
                  type={isMaskedValue(alpaca.apiSecret) ? 'password' : 'text'}
                  value={alpaca.apiSecret}
                  onFocus={() => clearMaskedField(setAlpaca, 'apiSecret')}
                  onChange={(e) => setAlpaca({ ...alpaca, apiSecret: e.target.value })}
                />
                <label className="vd-checkbox-row">
                  <input
                    type="checkbox"
                    checked={alpaca.isPaper}
                    onChange={(e) => setAlpaca({ ...alpaca, isPaper: e.target.checked })}
                  />
                  <span>
                    <strong>Paper trading account</strong>
                    <small className="vd-muted">Simulated Alpaca environment — not real money</small>
                  </span>
                </label>
              </div>
            )}

            {brokerId === 'ibkr' && (
              <div className="vd-form-stack">
                <input
                  placeholder="Flex Token"
                  value={ibkr.flexToken}
                  onFocus={() => clearMaskedField(setIbkr, 'flexToken')}
                  onChange={(e) => setIbkr({ ...ibkr, flexToken: e.target.value })}
                />
                <input
                  placeholder="Flex Query ID"
                  value={ibkr.flexQueryId}
                  onFocus={() => clearMaskedField(setIbkr, 'flexQueryId')}
                  onChange={(e) => setIbkr({ ...ibkr, flexQueryId: e.target.value })}
                />
              </div>
            )}

            {brokerId === 'ninjatrader' && (
              <div className="vd-form-stack">
                {tradovateOAuthConfigured && (
                  <button
                    type="button"
                    className="vd-ninja-alt-link vd-ninja-alt-link-block"
                    onClick={() => {
                      setNinjaView('portal');
                      setError('');
                      setMessage('');
                    }}
                    disabled={busy}
                  >
                    Use OAuth connection instead
                  </button>
                )}

                <span className="vd-control-label">Account Mode</span>
                <div className="vd-ninja-mode-toggle" role="group" aria-label="Tradovate account mode">
                  <button
                    type="button"
                    className={`vd-ninja-mode-btn${!ninja.isDemo ? ' active' : ''}${selectedStatus.connections?.live?.configured ? ' connected' : ''}`}
                    onClick={() => setNinja({ ...ninja, isDemo: false })}
                    disabled={busy}
                  >
                    Live
                    {selectedStatus.connections?.live?.configured ? (
                      <span className="vd-ninja-mode-btn-tag">Connected</span>
                    ) : null}
                  </button>
                  <button
                    type="button"
                    className={`vd-ninja-mode-btn${ninja.isDemo ? ' active' : ''}${selectedStatus.connections?.demo?.configured ? ' connected' : ''}`}
                    onClick={() => setNinja({ ...ninja, isDemo: true })}
                    disabled={busy}
                  >
                    Demo / Sim
                    {selectedStatus.connections?.demo?.configured ? (
                      <span className="vd-ninja-mode-btn-tag">Connected</span>
                    ) : null}
                  </button>
                </div>

                {(selectedStatus.connections?.live?.configured || selectedStatus.connections?.demo?.configured) && (
                  <div className="vd-ninja-mode-badges">
                    {selectedStatus.connections?.live?.configured ? (
                      <span className={`vd-ninja-mode-badge ${selectedStatus.connections.live.needsReconnect ? 'expired' : 'live'}`}>
                        <IconCheck size={12} />
                        {selectedStatus.connections.live.needsReconnect ? ' Live session expired' : ' Live connected'}
                      </span>
                    ) : null}
                    {selectedStatus.connections?.demo?.configured ? (
                      <span className={`vd-ninja-mode-badge ${selectedStatus.connections.demo.needsReconnect ? 'expired' : 'demo'}`}>
                        <IconCheck size={12} />
                        {selectedStatus.connections.demo.needsReconnect ? ' Demo session expired' : ' Demo connected'}
                      </span>
                    ) : null}
                  </div>
                )}

                {/* An expired OAuth session otherwise reads as "no trades" —
                    say what happened and that one click fixes it. */}
                {selectedStatus.needsReconnect ? (
                  <p className="vd-ninja-reconnect-note">
                    Your Tradovate sign-in has expired, so new trades stopped syncing.
                    Reconnect below to resume — your existing history is kept.
                  </p>
                ) : null}

                <p className="vd-muted vd-ninja-api-intro">
                  Enter Tradovate API credentials from Application Settings → API Access.
                </p>
                <input
                  placeholder="Tradovate username"
                  value={ninja.username}
                  onFocus={() => clearMaskedField(setNinja, 'username')}
                  onChange={(e) => setNinja({ ...ninja, username: e.target.value })}
                />
                <input
                  placeholder="Tradovate password"
                  type="password"
                  value={ninja.password}
                  onFocus={() => clearMaskedField(setNinja, 'password')}
                  onChange={(e) => setNinja({ ...ninja, password: e.target.value })}
                />
                <input
                  placeholder="API Key ID (CID)"
                  value={ninja.apiCid}
                  onFocus={() => clearMaskedField(setNinja, 'apiCid')}
                  onChange={(e) => setNinja({ ...ninja, apiCid: e.target.value })}
                />
                <input
                  placeholder="API Secret"
                  type={isMaskedValue(ninja.apiSec) ? 'password' : 'text'}
                  value={ninja.apiSec}
                  onFocus={() => clearMaskedField(setNinja, 'apiSec')}
                  onChange={(e) => setNinja({ ...ninja, apiSec: e.target.value })}
                />
              </div>
            )}

            <div className="vd-manual-import-row">
              <input
                ref={csvInputRef}
                type="file"
                accept=".csv,text/csv"
                className="vd-hidden-file-input"
                onChange={handleCsvSelected}
              />
              <button
                type="button"
                className="vd-manual-import-btn"
                disabled={busy}
                onClick={() => csvInputRef.current?.click()}
              >
                <IconUpload size={18} />
                <span>
                  <strong>Upload CSV</strong>
                  <small className="vd-muted">
                    {brokerId === 'ibkr'
                      ? 'IBKR Flex export with executions'
                      : 'Symbol, Side, Qty, Price, Date/Time columns'}
                  </small>
                </span>
              </button>
              <button
                type="button"
                className="vd-manual-import-btn"
                disabled={busy}
                onClick={() => setManualModalOpen(true)}
              >
                <IconPencil size={18} />
                <span>
                  <strong>Manual entry</strong>
                  <small className="vd-muted">Add a single round-trip trade</small>
                </span>
              </button>
            </div>

            <ManualTradeModal
              isOpen={manualModalOpen}
              brokerLabel={selectedBroker?.label}
              onClose={() => setManualModalOpen(false)}
              onSubmit={handleManualSubmit}
              busy={busy}
            />

            {error && <p className="vd-error">{error}</p>}
            {message && <p className="vd-success">{message}</p>}

            <footer className="vd-broker-detail-footer">
              {selectedStatus.configured && brokerId !== 'ninjatrader' && (
                <button type="button" className="vd-btn danger" onClick={() => handleRemoveAccount()} disabled={busy}>
                  <IconTrash size={15} /> Remove account
                </button>
              )}
              {brokerId === 'ninjatrader' && (selectedStatus.connections?.[ninja.isDemo ? 'demo' : 'live']?.configured) && (
                <button
                  type="button"
                  className="vd-btn danger"
                  onClick={() => handleRemoveAccount(ninja.isDemo ? 'demo' : 'live')}
                  disabled={busy}
                >
                  <IconTrash size={15} /> Remove {ninja.isDemo ? 'Demo' : 'Live'} account
                </button>
              )}
              <div className="vd-broker-detail-footer-actions">
                <button type="button" className="vd-btn" onClick={handleConnect} disabled={busy}>
                  {busy ? 'Syncing...' : (
                    <>
                      <IconRefresh size={16} /> {selectedStatus.configured ? 'Sync saved connection' : 'Connect & Sync'}
                    </>
                  )}
                </button>
                <button type="button" className="vd-btn secondary" onClick={backToPicker}>Back</button>
              </div>
            </footer>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
